import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useCurrency } from "@/lib/currency";
import { useTranslation } from "react-i18next";

function HelpDot({ tip }) {
  if (!tip) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="?" className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-zinc-400 text-zinc-500 text-[8px] font-bold mx-1 hover:bg-black hover:text-white hover:border-black transition shrink-0">?</button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function IcoDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const { fmt } = useCurrency();
  const { t } = useTranslation();
  const fmtMoney = (n) => fmt(n);
  const fmtNum = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 4 });
  const navigate = useNavigate();
  const [ico, setIco] = useState(null);
  const [property, setProperty] = useState(null);
  const [qty, setQty] = useState(1000);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/icos/${id}`);
    setIco(data);
    if (data.property_id) {
      const { data: p } = await api.get(`/properties/${data.property_id}`);
      setProperty(p);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!ico) return <div className="p-12 font-mono-label text-xs">Loading…</div>;

  const remaining = ico.total_coins - ico.coins_sold;
  const cost = qty * ico.price_per_coin;
  const monthlyReturn = qty * ico.monthly_return_per_coin;
  const totalReturn = monthlyReturn * ico.duration_months;
  const apy = ico.expected_annual_return_pct;

  const buy = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "investor") { toast.error("Only investors can buy coins"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/icos/${id}/buy`, { quantity: Number(qty) });
      toast.success(data.fully_funded ? "ICO fully funded! Owner has been paid." : `Bought ${qty} coins`);
      await load();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const feas = property?.feasibility;
  const units = property?.units || [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-12 gap-8">
      <div className="col-span-12 md:col-span-7">
        <div className="aspect-[16/10] overflow-hidden border border-black/10 mb-6">
          <img src={ico.property_image} alt="" className="w-full h-full object-cover" />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Badge className={`rounded-none font-mono-label text-[10px] ${
            ico.status === "active" ? "bg-[#002FA7]" : "bg-black"
          }`}>{ico.status}</Badge>
          {apy != null && (
            <Badge className="rounded-none bg-[#FFCC00] text-black font-mono-label text-[10px] flex items-center gap-1">
              {apy.toFixed(2)}% target APY
              <HelpDot tip={t("tip_ico_apy")} />
            </Badge>
          )}
          <span className="font-mono-label text-[10px] text-zinc-500">
            Owner: <b className="text-black">{ico.owner_name}</b>
            {ico.amc_name && <> · AMC: <b className="text-black">{ico.amc_name}</b></>}
          </span>
        </div>

        <h1 className="font-display text-5xl font-black mt-3 tracking-tight">{ico.property_name}</h1>
        {property?.description && <p className="text-zinc-600 mt-3">{property.description}</p>}

        {/* Big numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 mt-8 border border-black/10">
          {[
            ["Aggregate rent / mo", fmtMoney(ico.monthly_rent), t("tip_ico_rent")],
            ["Duration", `${ico.duration_months} mo`, t("tip_ico_duration")],
            ["Total coins", ico.total_coins.toLocaleString(), t("tip_ico_coins")],
            ["Price / coin", fmtMoney(ico.price_per_coin), t("tip_ico_price")],
            ["Return / coin / mo", `$${fmtNum(ico.monthly_return_per_coin)}`, t("tip_ico_return")],
            ["Total raise target", fmtMoney(ico.target_raise), t("tip_ico_raise")],
            ["Coins sold", ico.coins_sold.toLocaleString(), t("tip_ico_sold")],
            ["Distributed (cum.)", fmtMoney(ico.total_distributed), t("tip_ico_distributed")],
          ].map(([k, v, tip]) => (
            <div key={k} className="bg-white p-5 min-w-0">
              <div className="font-mono-label text-[9px] text-zinc-500 flex items-center"><span className="truncate">{k}</span><HelpDot tip={tip}/></div>
              <div className="font-display text-xl font-bold mt-1 tabular-nums truncate">{v}</div>
            </div>
          ))}
        </div>

        {/* Units table */}
        {units.length > 0 && (
          <div className="mt-10">
            <div className="font-mono-label text-[10px] text-[#002FA7] mb-2">⎯⎯ Units in this facility</div>
            <div className="border border-black/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-black/10">
                  <tr className="font-mono-label text-[10px] text-left">
                    <th className="p-3">Unit</th>
                    <th className="p-3 text-right">Monthly rent</th>
                    <th className="p-3">Tenant</th>
                    <th className="p-3">Lease end</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u, i) => (
                    <tr key={`${u.name}-${i}`} className="border-b border-black/5">
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-right">{fmtMoney(u.monthly_rent)}</td>
                      <td className="p-3 text-zinc-600">{u.tenant_name || "—"}</td>
                      <td className="p-3 text-zinc-600">{u.lease_end || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Feasibility study */}
        {feas && (
          <div className="mt-10 border border-black p-6 bg-zinc-50">
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div>
                <div className="font-mono-label text-[10px] text-[#002FA7]">⎯⎯ Feasibility study</div>
                <div className="font-display text-xl font-bold mt-1">By {feas.amc_name}</div>
              </div>
              <Badge className="rounded-none bg-[#FFCC00] text-black font-mono-label text-[10px]">approved</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5 text-sm">
              {[
                ["Historical annual rent", fmtMoney(feas.historical_annual_rent)],
                ["Expected occupancy", `${feas.expected_occupancy_pct.toFixed(1)}%`],
                ["Rent growth / yr", `${feas.expected_rent_growth_pct.toFixed(1)}%`],
                ["Maintenance / mo", fmtMoney(feas.monthly_maintenance_cost)],
                ["Effective rent / mo", fmtMoney(feas.effective_monthly_rent)],
                ["Net to investors / mo", fmtMoney(feas.net_monthly_to_investors)],
                ["AMC monthly fee", `${feas.amc_monthly_fee_pct.toFixed(2)}%`],
                ["AMC setup fee", `${feas.amc_setup_fee_pct.toFixed(2)}%`],
                ["Target APY", `${feas.expected_annual_return_pct.toFixed(2)}%`],
              ].map(([k, v]) => (
                <div key={k} className="border border-black/10 p-3 bg-white">
                  <div className="font-mono-label text-[9px] text-zinc-500">{k}</div>
                  <div className="font-display font-bold mt-1">{v}</div>
                </div>
              ))}
            </div>
            {feas.comment && (
              <div className="mt-4 text-sm text-zinc-700 italic border-l-4 border-[#002FA7] pl-4">
                &ldquo;{feas.comment}&rdquo;
              </div>
            )}
          </div>
        )}

        <div className="mt-10 border border-black p-6 bg-white">
          <div className="font-mono-label text-[10px] text-[#002FA7]">⎯⎯ How returns work</div>
          <p className="mt-3 text-sm text-zinc-700 leading-relaxed">
            Each coin earns <b>{fmtMoney(ico.monthly_return_per_coin)}</b> per month for up to <b>{ico.duration_months}</b> months.
            Expected total per coin: <b>{fmtMoney(ico.monthly_return_per_coin * ico.duration_months)}</b> on a <b>{fmtMoney(ico.price_per_coin)}</b> entry
            {apy != null && <> · targeted APY <b>{apy.toFixed(2)}%</b></>}.
            Months elapsed: <b>{ico.months_elapsed}</b> / {ico.duration_months}.
          </p>
        </div>
      </div>

      <div className="col-span-12 md:col-span-5">
        <div className="border border-black p-6 sticky top-24">
          <div className="font-mono-label text-[10px] text-[#002FA7]">⎯⎯ Buy coins</div>
          <h3 className="font-display text-2xl font-bold mt-2">Invest</h3>
          {apy != null && (
            <div className="mt-2 inline-block bg-[#FFCC00] text-black px-3 py-1 font-mono-label text-[10px]">
              TARGET APY · {apy.toFixed(2)}%
            </div>
          )}

          <div className="my-6">
            <div className="flex justify-between font-mono-label text-[10px] mb-2">
              <span>{ico.coins_sold.toLocaleString()} / {ico.total_coins.toLocaleString()}</span>
              <span className="text-[#002FA7]">{remaining.toLocaleString()} left</span>
            </div>
            <div className="h-1 bg-black/10">
              <div className="h-full bg-[#002FA7]" style={{ width: `${(ico.coins_sold/ico.total_coins)*100}%` }}/>
            </div>
            {ico.investor_count > 0 && (
              <div
                data-testid="ico-investor-badge"
                className="mt-3 flex items-center gap-2 text-[10px] font-mono-label text-zinc-700 border border-black/10 bg-zinc-50 px-2.5 py-1.5"
              >
                <span className="inline-flex -space-x-1">
                  {[...Array(Math.min(3, ico.investor_count))].map((_, idx) => (
                    <span
                      key={`avd-${idx}`}
                      className="inline-block w-4 h-4 rounded-full border border-white"
                      style={{ backgroundColor: ["#002FA7", "#FFCC00", "#FF3B30"][idx] || "#002FA7" }}
                    />
                  ))}
                </span>
                <span className="tabular-nums">{t("mp_investors", { count: ico.investor_count })}</span>
              </div>
            )}
          </div>

          <label className="font-mono-label text-[10px]">Quantity</label>
          <Input data-testid="buy-qty" type="number" min={1} max={remaining} value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
            className="rounded-none h-11 mt-2"/>

          <div className="mt-5 space-y-2 text-sm border-t border-black/10 pt-4">
            <div className="flex justify-between"><span className="text-zinc-500">Cost now</span><b>{fmtMoney(cost)}</b></div>
            <div className="flex justify-between"><span className="text-zinc-500">Est. monthly</span><b>{fmtMoney(monthlyReturn)}</b></div>
            <div className="flex justify-between"><span className="text-zinc-500">Est. total over term</span><b className="text-[#002FA7]">{fmtMoney(totalReturn)}</b></div>
          </div>

          <Button data-testid="buy-submit" onClick={buy} disabled={busy || ico.status !== "active"}
            className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F70] h-12 mt-6 font-mono-label text-xs">
            {ico.status !== "active" ? "Not active" : busy ? "Processing…" : `Buy ${qty.toLocaleString()} coins →`}
          </Button>
          {!user && <div className="text-xs text-zinc-500 mt-3">Sign in as an investor to buy.</div>}
        </div>
      </div>
    </div>
  );
}
